import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import {
  ArrowLeft, Music2, Play, Pause, SkipForward, Square, Volume2,
  Radio, RefreshCcw, Plus, ExternalLink, Loader2, Trash2,
  Heart, ListMusic, Clock, Search, LogOut, Zap,
} from "lucide-react";

const SOURCE_LOGOS = {
  spotify: "SPOTIFY",
  "spotify→soundcloud": "SPOTIFY→SC",
  "spotify→youtube": "SPOTIFY→YT",
  direct: "DIRECT",
  "yt-dlp": "STREAM",
};

function fmtDur(sec) {
  if (!sec || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}:${(m % 60).toString().padStart(2, "0")}:${s}` : `${m}:${s}`;
}

export default function Music() {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [selectedCh, setSelectedCh] = useState("");
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enqueuing, setEnqueuing] = useState(false);
  const [guildName, setGuildName] = useState("");
  const pollRef = useRef(null);

  // Spotify state
  const [spotify, setSpotify] = useState({ connected: false });
  const [spotifyTab, setSpotifyTab] = useState("playlists"); // playlists | liked | recent | search
  const [playlists, setPlaylists] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [activePlaylist, setActivePlaylist] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [libLoading, setLibLoading] = useState(false);

  const loadInitial = async () => {
    try {
      const [chRes, guild, stat, spot] = await Promise.all([
        api.get(`/guilds/${guildId}/voice-channels`),
        api.get(`/guilds/${guildId}`).catch(() => ({ data: null })),
        api.get(`/guilds/${guildId}/music/status`),
        api.get(`/spotify/me`),
      ]);
      setChannels(chRes.data.channels || []);
      setStatus(stat.data);
      setGuildName(guild.data?.guild?.name || "");
      setSpotify(spot.data);
      if (!selectedCh) {
        const connected = stat.data?.channel_id;
        const first = chRes.data.channels?.[0]?.id;
        setSelectedCh(connected || first || "");
      }
    } catch (e) {
      toast.error("Failed to load music state");
    } finally {
      setLoading(false);
    }
  };

  const pollStatus = async () => {
    try {
      const r = await api.get(`/guilds/${guildId}/music/status`);
      setStatus(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadInitial();
    pollRef.current = setInterval(pollStatus, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [guildId]);

  const play = async () => {
    if (!url.trim()) return toast.error("Paste a link first");
    if (!selectedCh) return toast.error("Pick a voice channel");
    setEnqueuing(true);
    try {
      const r = await api.post(`/guilds/${guildId}/music/play`, { url: url.trim(), channel_id: selectedCh });
      toast.success(`Queued · ${r.data.track.title}`);
      setUrl("");
      setStatus(r.data.status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue track");
    } finally {
      setEnqueuing(false);
    }
  };

  const skip = async () => {
    try { const r = await api.post(`/guilds/${guildId}/music/skip`); setStatus(r.data); toast.success("Skipped"); }
    catch { toast.error("Skip failed"); }
  };
  const pause = async () => {
    try { const r = await api.post(`/guilds/${guildId}/music/pause`); setStatus(r.data); }
    catch { toast.error("Pause failed"); }
  };
  const resume = async () => {
    try { const r = await api.post(`/guilds/${guildId}/music/resume`); setStatus(r.data); }
    catch { toast.error("Resume failed"); }
  };
  const stop = async () => {
    if (!confirm("Stop playback and disconnect?")) return;
    try { const r = await api.post(`/guilds/${guildId}/music/stop`); setStatus(r.data); toast.success("Stopped"); }
    catch { toast.error("Stop failed"); }
  };

  // ---- Spotify ----
  const spotifyLogin = async () => {
    try {
      const r = await api.get("/spotify/login-url");
      localStorage.setItem("spotify_return_to", `/music/${guildId}`);
      window.location.href = r.data.url;
    } catch { toast.error("Failed to start Spotify login"); }
  };

  const spotifyDisconnect = async () => {
    if (!confirm("Disconnect Spotify?")) return;
    try {
      await api.delete("/spotify/disconnect");
      setSpotify({ connected: false });
      setPlaylists([]); setTracks([]); setActivePlaylist(null);
      toast.success("Spotify disconnected");
    } catch { toast.error("Failed"); }
  };

  const loadSpotifyTab = async (tab) => {
    setSpotifyTab(tab); setActivePlaylist(null); setTracks([]);
    setLibLoading(true);
    try {
      if (tab === "playlists") {
        const r = await api.get("/spotify/playlists");
        setPlaylists(r.data.playlists || []);
      } else if (tab === "liked") {
        const r = await api.get("/spotify/liked");
        setTracks(r.data.tracks || []);
      } else if (tab === "recent") {
        const r = await api.get("/spotify/recent");
        setTracks(r.data.tracks || []);
      } else if (tab === "top") {
        const r = await api.get("/spotify/top");
        setTracks(r.data.tracks || []);
      }
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Spotify session expired — reconnect"); setSpotify({ connected: false }); }
      else toast.error("Failed to load library");
    } finally { setLibLoading(false); }
  };

  const openPlaylist = async (p) => {
    setActivePlaylist(p);
    setLibLoading(true);
    try {
      const r = await api.get(`/spotify/playlists/${p.id}/tracks`);
      setTracks(r.data.tracks || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to load tracks";
      toast.error(detail);
      setTracks([]);
    }
    finally { setLibLoading(false); }
  };

  const doSearch = async () => {
    if (!searchQ.trim() || searchQ.length < 2) return;
    setSpotifyTab("search"); setActivePlaylist(null);
    setLibLoading(true);
    try {
      const r = await api.get(`/spotify/search`, { params: { q: searchQ } });
      setTracks(r.data.tracks || []);
    } catch { toast.error("Search failed"); }
    finally { setLibLoading(false); }
  };

  const playSpotifyTrack = async (t) => {
    if (!selectedCh) return toast.error("Pick a voice channel first");
    try {
      const r = await api.post(`/guilds/${guildId}/music/play-spotify`, {
        channel_id: selectedCh,
        search_query: t.search_query,
        name: t.name,
        artists: t.artists,
        thumbnail: t.thumbnail,
        spotify_url: t.spotify_url,
      });
      toast.success(`Queued · ${t.name}`);
      setStatus(r.data.status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue");
    }
  };

  const cur = status?.current;
  const queue = status?.queue || [];
  const connected = status?.connected;

  return (
    <div className="min-h-screen bg-[#06070A] mm-grid-bg text-white" data-testid="music-page">
      <div className="absolute inset-0 mm-noise pointer-events-none" />

      {/* Top bar */}
      <div className="relative z-10 border-b border-[#8B5CF6]/20 bg-gradient-to-r from-[#8B5CF6]/5 to-transparent flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <button
            data-testid="music-back-btn"
            onClick={() => navigate(`/dashboard/${guildId}`)}
            className="p-2 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Music2 className="w-4 h-4 text-[#8B5CF6]" />
              <span className="font-display font-black tracking-tight">MUSIC · JUKEBOX</span>
            </div>
            <div className="font-mono text-[10px] text-[#64748B] tracking-widest">
              {guildName ? guildName.toUpperCase() : "SERVER"} · YT · SOUNDCLOUD · SPOTIFY · DIRECT
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-white/10">
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[#10B981] shadow-[0_0_6px_#10B981]" : "bg-[#64748B]"}`} />
            <span className="font-mono text-[10px] tracking-widest text-[#94A3B8]">
              {connected ? `IN ${status?.channel_name?.toUpperCase() || "VC"}` : "IDLE"}
            </span>
          </div>
          <button
            data-testid="music-refresh-btn"
            onClick={loadInitial}
            className="p-2 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6]"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Input + VC picker */}
        <div className="border border-white/10 bg-[#0D0E15] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-4 h-4 text-[#8B5CF6]" />
            <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Drop a link</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_240px_auto] gap-3">
            <input
              data-testid="music-url-input"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !enqueuing && play()}
              placeholder="YouTube · SoundCloud · Spotify · direct .mp3 URL..."
              className="bg-[#06070A] border border-white/10 px-3 py-3 text-sm outline-none focus:border-[#8B5CF6] font-mono"
            />
            <select
              data-testid="music-vc-picker"
              value={selectedCh}
              onChange={e => setSelectedCh(e.target.value)}
              className="bg-[#06070A] border border-white/10 px-3 py-3 text-sm outline-none focus:border-[#8B5CF6] appearance-none cursor-pointer"
            >
              {channels.length === 0 && <option value="">— no voice channels —</option>}
              {channels.map(c => (
                <option key={c.id} value={c.id}>
                  {c.type === "stage" ? "🎙 " : "🔊 "}
                  {c.name} {c.members ? ` · ${c.members} in` : ""}
                </option>
              ))}
            </select>
            <button
              data-testid="music-play-btn"
              onClick={play}
              disabled={enqueuing || !url || !selectedCh}
              className="bg-[#10B981] text-black font-display font-bold px-6 py-3 hover:bg-[#10B981]/80 disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {enqueuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {enqueuing ? "RESOLVING..." : "QUEUE"}
            </button>
          </div>
          <div className="mt-3 font-mono text-[10px] text-[#64748B] tracking-widest leading-relaxed">
            // best sources: <span className="text-[#10B981]">soundcloud</span> · <span className="text-[#10B981]">bandcamp</span> · <span className="text-[#10B981]">direct .mp3/.ogg urls</span><br />
            // spotify links auto-resolve via soundcloud (youtube fallback) · youtube direct links may fail from this host due to yt anti-bot
          </div>
        </div>

        {/* Spotify library */}
        <SpotifyPanel
          spotify={spotify}
          onLogin={spotifyLogin}
          onDisconnect={spotifyDisconnect}
          tab={spotifyTab}
          setTab={loadSpotifyTab}
          playlists={playlists}
          tracks={tracks}
          activePlaylist={activePlaylist}
          setActivePlaylist={setActivePlaylist}
          openPlaylist={openPlaylist}
          searchQ={searchQ}
          setSearchQ={setSearchQ}
          onSearch={doSearch}
          libLoading={libLoading}
          onPlayTrack={playSpotifyTrack}
        />

        {/* Now playing */}
        <div className="border border-white/10 bg-gradient-to-br from-[#8B5CF6]/5 via-[#0D0E15] to-[#0D0E15] p-6" data-testid="now-playing">
          <div className="flex items-center gap-2 mb-4">
            <Volume2 className="w-4 h-4 text-[#8B5CF6]" />
            <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Now Playing</span>
            {status?.is_paused && (
              <span className="ml-2 px-2 py-0.5 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] font-mono text-[9px] tracking-widest">PAUSED</span>
            )}
          </div>

          {loading ? (
            <div className="font-mono text-sm text-[#8B5CF6]">// loading player...</div>
          ) : cur ? (
            <div className="flex flex-col md:flex-row gap-5">
              {cur.thumbnail ? (
                <img src={cur.thumbnail} alt="" className="w-full md:w-48 h-48 object-cover rounded-md border border-white/10" />
              ) : (
                <div className="w-full md:w-48 h-48 bg-[#06070A] border border-white/10 flex items-center justify-center">
                  <Music2 className="w-12 h-12 text-[#8B5CF6]/50" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <a
                  href={cur.webpage_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-display font-black text-2xl md:text-3xl leading-tight break-words hover:text-[#8B5CF6] transition-colors inline-flex items-start gap-2"
                >
                  {cur.title}
                  <ExternalLink className="w-4 h-4 mt-2 opacity-50" />
                </a>
                <div className="text-sm text-[#94A3B8] mt-1">{cur.uploader}</div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="px-2 py-0.5 border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#8B5CF6] font-mono text-[9px] tracking-widest">
                    {SOURCE_LOGOS[cur.source] || cur.source?.toUpperCase()}
                  </span>
                  <span className="px-2 py-0.5 border border-white/10 bg-[#06070A] font-mono text-[9px] tracking-widest text-[#94A3B8]">
                    {fmtDur(cur.duration)}
                  </span>
                  {cur.requested_by && (
                    <span className="font-mono text-[10px] text-[#64748B]">req by {cur.requested_by}</span>
                  )}
                </div>

                {/* Controls */}
                <div className="flex flex-wrap gap-2 mt-5">
                  {status?.is_paused ? (
                    <button
                      data-testid="music-resume-btn"
                      onClick={resume}
                      className="px-4 py-2.5 bg-[#10B981] text-black font-display font-bold flex items-center gap-2 hover:bg-[#10B981]/80"
                    ><Play className="w-4 h-4" />RESUME</button>
                  ) : (
                    <button
                      data-testid="music-pause-btn"
                      onClick={pause}
                      className="px-4 py-2.5 border border-[#F59E0B]/40 hover:border-[#F59E0B] hover:bg-[#F59E0B]/10 text-[#F59E0B] font-mono text-xs tracking-widest flex items-center gap-2"
                    ><Pause className="w-4 h-4" />PAUSE</button>
                  )}
                  <button
                    data-testid="music-skip-btn"
                    onClick={skip}
                    className="px-4 py-2.5 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6] font-mono text-xs tracking-widest flex items-center gap-2"
                  ><SkipForward className="w-4 h-4" />SKIP</button>
                  <button
                    data-testid="music-stop-btn"
                    onClick={stop}
                    className="px-4 py-2.5 border border-[#EF4444]/40 hover:border-[#EF4444] hover:bg-[#EF4444]/10 text-[#EF4444] font-mono text-xs tracking-widest flex items-center gap-2"
                  ><Square className="w-4 h-4" />STOP · DISCONNECT</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="font-mono text-sm text-[#64748B] py-8 text-center">
              // nothing playing · paste a link above to get started
            </div>
          )}
        </div>

        {/* Queue */}
        <div className="border border-white/10 bg-[#0D0E15]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">
              Queue · {queue.length}
            </div>
            {queue.length > 0 && (
              <button
                data-testid="music-clear-queue"
                onClick={stop}
                className="font-mono text-[10px] tracking-widest text-[#EF4444] hover:text-[#FF3377] flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> CLEAR
              </button>
            )}
          </div>
          <div className="max-h-[50vh] overflow-y-auto" data-testid="music-queue-list">
            {queue.length === 0 && (
              <div className="p-6 text-center font-mono text-xs text-[#64748B]">// queue empty</div>
            )}
            {queue.map((t, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3 border-b border-white/5 hover:bg-[#141622]">
                <div className="font-mono text-[10px] text-[#64748B] w-6 shrink-0">{(i + 1).toString().padStart(2, "0")}</div>
                {t.thumbnail ? (
                  <img src={t.thumbnail} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 bg-[#06070A] flex items-center justify-center shrink-0">
                    <Music2 className="w-4 h-4 text-[#8B5CF6]/50" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.title}</div>
                  <div className="text-xs text-[#94A3B8] truncate">{t.uploader}</div>
                </div>
                <span className="font-mono text-[10px] text-[#94A3B8]">{fmtDur(t.duration)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SpotifyPanel({
  spotify, onLogin, onDisconnect, tab, setTab, playlists, tracks, activePlaylist,
  setActivePlaylist, openPlaylist, searchQ, setSearchQ, onSearch, libLoading, onPlayTrack,
}) {
  if (!spotify?.connected) {
    return (
      <div className="border border-[#1DB954]/30 bg-gradient-to-br from-[#1DB954]/10 via-[#0D0E15] to-[#0D0E15] p-6" data-testid="spotify-connect">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#1DB954] flex items-center justify-center">
            <Music2 className="w-5 h-5 text-black" />
          </div>
          <div>
            <div className="font-display font-black text-lg">CONNECT SPOTIFY</div>
            <div className="text-xs text-[#94A3B8]">Browse your playlists, liked songs & recent tracks — play them in VC</div>
          </div>
        </div>
        <button
          data-testid="spotify-login-btn"
          onClick={onLogin}
          className="px-5 py-2.5 bg-[#1DB954] hover:bg-[#1ed760] text-black font-display font-bold flex items-center gap-2"
        >
          <Zap className="w-4 h-4" /> LOGIN WITH SPOTIFY
        </button>
      </div>
    );
  }

  const TABS = [
    { id: "playlists", label: "PLAYLISTS", icon: ListMusic },
    { id: "liked",     label: "LIKED",     icon: Heart },
    { id: "recent",    label: "RECENT",    icon: Clock },
    { id: "top",       label: "TOP",       icon: Zap },
    { id: "search",    label: "SEARCH",    icon: Search },
  ];

  return (
    <div className="border border-[#1DB954]/40 bg-[#0D0E15]" data-testid="spotify-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-gradient-to-r from-[#1DB954]/10 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-[#1DB954] flex items-center justify-center">
            <Music2 className="w-4 h-4 text-black" />
          </div>
          <div>
            <div className="font-display font-bold text-sm">SPOTIFY LIBRARY</div>
            <div className="font-mono text-[10px] text-[#64748B]">
              {spotify.display_name || spotify.spotify_id} · {(spotify.product || "").toUpperCase()}
            </div>
          </div>
        </div>
        <button
          data-testid="spotify-disconnect-btn"
          onClick={onDisconnect}
          title="Disconnect"
          className="p-2 border border-white/10 hover:border-[#EF4444] hover:text-[#EF4444]"
        >
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`spotify-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-widest transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-[#1DB954]/15 text-[#1DB954] border border-[#1DB954]/50"
                : "text-[#94A3B8] hover:text-white border border-transparent"
            }`}
          >
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Search bar (only visible on search tab) */}
      {tab === "search" && (
        <div className="p-3 border-b border-white/5 flex gap-2">
          <input
            data-testid="spotify-search-input"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && onSearch()}
            placeholder="Search Spotify catalog..."
            className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#1DB954] font-mono"
          />
          <button
            data-testid="spotify-search-btn"
            onClick={onSearch}
            className="px-4 py-2 bg-[#1DB954] hover:bg-[#1ed760] text-black font-display font-bold text-sm"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Content */}
      <div className="max-h-[520px] overflow-y-auto" data-testid="spotify-content">
        {libLoading && (
          <div className="p-6 text-center font-mono text-xs text-[#1DB954]">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" />loading...
          </div>
        )}

        {!libLoading && tab === "playlists" && !activePlaylist && (
          <>
            {playlists.length === 0 && (
              <div className="p-8 text-center font-mono text-xs text-[#64748B]">
                // pick a tab to load your library, or tap PLAYLISTS again
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {playlists.map(p => (
                <button
                  key={p.id}
                  data-testid={`spotify-playlist-${p.id}`}
                  onClick={() => openPlaylist(p)}
                  className="flex items-center gap-3 p-3 border-b border-white/5 hover:bg-[#141622] text-left transition-colors"
                >
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-[#06070A] flex items-center justify-center shrink-0">
                      <ListMusic className="w-4 h-4 text-[#1DB954]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-[#94A3B8] truncate">
                      {p.tracks_total} tracks · {p.owner}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {!libLoading && activePlaylist && (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#06070A]/50">
              <button
                data-testid="spotify-playlist-back"
                onClick={() => setActivePlaylist(null)}
                className="p-1.5 border border-white/10 hover:border-[#1DB954] hover:text-[#1DB954]"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
              {activePlaylist.thumbnail && (
                <img src={activePlaylist.thumbnail} alt="" className="w-10 h-10 rounded object-cover" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate">{activePlaylist.name}</div>
                <div className="font-mono text-[10px] text-[#64748B]">{tracks.length} tracks</div>
              </div>
            </div>
            <TrackList tracks={tracks} onPlayTrack={onPlayTrack} />
          </>
        )}

        {!libLoading && ["liked", "recent", "top", "search"].includes(tab) && (
          <TrackList tracks={tracks} onPlayTrack={onPlayTrack} emptyText={
            tab === "search" ? "// search for a song above" : "// nothing here yet"
          } />
        )}
      </div>
    </div>
  );
}

function TrackList({ tracks, onPlayTrack, emptyText = "// no tracks" }) {
  if (tracks.length === 0) {
    return <div className="p-8 text-center font-mono text-xs text-[#64748B]">{emptyText}</div>;
  }
  return (
    <>
      {tracks.map((t, i) => (
        <div
          key={t.id + "-" + i}
          className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 hover:bg-[#141622] group"
        >
          <div className="font-mono text-[10px] text-[#64748B] w-6 shrink-0">{(i + 1).toString().padStart(2, "0")}</div>
          {t.thumbnail ? (
            <img src={t.thumbnail} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
          ) : (
            <div className="w-10 h-10 bg-[#06070A] flex items-center justify-center shrink-0">
              <Music2 className="w-3.5 h-3.5 text-[#1DB954]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate text-sm">{t.name}</div>
            <div className="text-xs text-[#94A3B8] truncate">{t.artists}</div>
          </div>
          <button
            data-testid={`spotify-track-play-${t.id}`}
            onClick={() => onPlayTrack(t)}
            className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-[#1DB954] hover:bg-[#1ed760] text-black font-mono text-[10px] tracking-widest font-bold flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> QUEUE
          </button>
        </div>
      ))}
    </>
  );
}

